import { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  getSession,
  setSession as setSessionStore,
  updateSession as updateSessionStore,
  clearSession as clearSessionStore,
  subscribeToSession,
} from "./sessionStore";
import AuthApi from "../api/auth/AuthenticationApi";
import { UserPrefsApi } from "../api/common/apiIndex";
import { useTheme } from "../theme/ThemeProvider";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const initialSession = getSession();
  const [session, setSession] = useState(() => initialSession);
  const [bootstrapped, setBootstrapped] = useState(() => Boolean(initialSession?.token));
  const { setTheme } = useTheme() || {};
  const location = useLocation();
  const prefsAppliedForRef = useRef(null);

  useEffect(() => subscribeToSession(setSession), []);

  useEffect(() => {
    if (bootstrapped || session?.token) return;
    const publicAuthPaths = [
      "/login",
      "/forgot-password",
      "/reset-password",
      "/business-register",
      "/verify-email",
      "/invite",
    ];
    if (publicAuthPaths.some((path) => location.pathname.startsWith(path))) {
      setBootstrapped(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await AuthApi.refresh();
      } catch {
        // ignore – user may simply not be logged in yet
        clearSessionStore();
      } finally {
        if (!cancelled) setBootstrapped(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bootstrapped, session?.token, location.pathname]);

  useEffect(() => {
    if (typeof setTheme !== "function") return;
    if (!session?.token) {
      prefsAppliedForRef.current = null;
      setTheme("system");
      return;
    }

    const preferenceOwner = session?.user?.id ?? session?.user?.uuid ?? "current-user";
    if (prefsAppliedForRef.current === preferenceOwner) return;

    let cancelled = false;
    (async () => {
      try {
        const prefs = await UserPrefsApi.getMyPrefs();
        const prefTheme = typeof prefs?.theme === "string" ? prefs.theme.toLowerCase() : null;
        const normalized =
          prefTheme === "dark" || prefTheme === "light" || prefTheme === "system"
            ? prefTheme
            : "system";
        if (!cancelled) {
          setTheme(normalized || "system");
          prefsAppliedForRef.current = preferenceOwner;
        }
      } catch {
        if (!cancelled) {
          setTheme("system");
          prefsAppliedForRef.current = preferenceOwner;
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [session?.token, session?.user?.id, session?.user?.uuid, setTheme]);

  const login = useCallback(
    ({ token, expiresAt, user, activeBusinessId, memberships }) => {
      setSessionStore({ token, expiresAt, user, activeBusinessId, memberships });
      setBootstrapped(true);
    },
    [setBootstrapped]
  );

  const logout = useCallback(() => {
    clearSessionStore();
    setBootstrapped(true);
  }, [setBootstrapped]);

  const setRoles = useCallback((newRoles) => {
    updateSessionStore((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        user: { ...prev.user, roles: Array.isArray(newRoles) ? newRoles : [] },
      };
    });
  }, []);

  const addRole = useCallback((role) => {
    updateSessionStore((prev) => {
      if (!prev) return prev;
      const roles = new Set(prev.user?.roles ?? []);
      roles.add(role);
      return { ...prev, user: { ...prev.user, roles: Array.from(roles) } };
    });
  }, []);

  const removeRole = useCallback((role) => {
    updateSessionStore((prev) => {
      if (!prev) return prev;
      const roles = (prev.user?.roles ?? []).filter((r) => r !== role);
      return { ...prev, user: { ...prev.user, roles } };
    });
  }, []);

  const updateUser = useCallback((patch) => {
    updateSessionStore((prev) => {
      if (!prev) return prev;
      return { ...prev, user: { ...prev.user, ...patch } };
    });
  }, []);

  const refreshSession = useCallback((payload) => {
    updateSessionStore((prev) => {
      if (!prev) return prev;
      const hasActiveBusinessId = Object.prototype.hasOwnProperty.call(payload, "activeBusinessId");
      return {
        ...prev,
        token: payload.token,
        expiresAt: payload.expiresAt,
        user: payload.user ?? prev.user,
        activeBusinessId: hasActiveBusinessId ? payload.activeBusinessId ?? null : prev.activeBusinessId ?? null,
        memberships: payload.memberships ?? prev.memberships ?? [],
      };
    });
  }, []);

  const switchBusiness = useCallback(async (businessId) => {
    const data = await AuthApi.switchBusiness({ businessId });
    setSessionStore({
      token: data.token,
      expiresAt: data.expiresAt,
      user: data.user,
      activeBusinessId: data.activeBusinessId,
      memberships: data.memberships,
    });
    setBootstrapped(true);
    return data;
  }, [setBootstrapped]);

  const memberships = useMemo(
    () => (Array.isArray(session?.memberships) ? session.memberships : []),
    [session?.memberships]
  );

  const activeBusinessId = session?.activeBusinessId ?? null;

  const activeMembership = useMemo(
    () => memberships.find((m) => String(m.businessId) === String(activeBusinessId)) ?? null,
    [memberships, activeBusinessId]
  );

  const roles = useMemo(() => {
    if (activeMembership && Array.isArray(activeMembership.roles)) {
      return activeMembership.roles;
    }
    return Array.isArray(session?.user?.roles) ? session.user.roles : [];
  }, [activeMembership, session?.user?.roles]);

  const value = useMemo(() => {
    const hasRole = (role) => roles.includes(role);
    const hasAnyRole = (list) => Array.isArray(list) && list.some((r) => roles.includes(r));
    const expiresAt = session?.expiresAt || 0;
    const isAuthenticated = Boolean(session?.token) && Date.now() < expiresAt;

    return {
      user: session?.user ?? null,
      roles,
      country: session?.user?.country ?? null,
      userId: session?.user?.id ?? null,
      token: session?.token ?? null,
      expiresAt,
      activeBusinessId,
      memberships,
      activeMembership,
      isAuthenticated,
      isBootstrapping: !bootstrapped,
      isAuthReady: bootstrapped,
      login,
      logout,
      setRoles,
      addRole,
      removeRole,
      updateUser,
      refreshSession,
      switchBusiness,
      hasRole,
      hasAnyRole,
    };
  }, [
    session,
    roles,
    bootstrapped,
    login,
    logout,
    setRoles,
    addRole,
    removeRole,
    updateUser,
    refreshSession,
    switchBusiness,
    activeBusinessId,
    memberships,
    activeMembership,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
